// Overview KPI cards (personal + team) and the Returns-throughput block.
// Extracted verbatim from StatisticsPage.tsx; aggregates computed from props.
import React from 'react';
import { KPICard, Session, ProblematicOrder } from './shared';

export default function OverviewSection({ rounds, problematic, retSpeed, isManager }: {
  rounds: Session[];
  problematic: ProblematicOrder[];
  retSpeed: any | null;
  isManager: boolean;
}) {
  const totalOrders  = rounds.reduce((s, x) => s + x.orders, 0);
  const totalRounds  = rounds.length;
  const measuredSess = rounds.filter(s => s.orders_per_hour !== null);
  const avgOPH = measuredSess.length > 0
    ? measuredSess.reduce((a, s) => a + (s.orders_per_hour || 0), 0) / measuredSess.length
    : null;
  const solvingTotal = rounds.reduce((s, x) => s + x.solving_orders, 0);

  const byPerson: Record<string, { name: string; orders: number; rounds: number }> = {};
  rounds.forEach(s => {
    if (!byPerson[s.username]) byPerson[s.username] = { name: s.display_name, orders: 0, rounds: 0 };
    byPerson[s.username].orders += s.orders;
    byPerson[s.username].rounds += 1;
  });
  const topPerson = Object.values(byPerson).sort((a, b) => b.orders - a.orders)[0];

  return (
    <>
      {/* KPI row 1 — personal */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <KPICard label="P&P Orders" value={String(totalOrders)} sub="total in period" />
        <KPICard label="Rounds" value={String(totalRounds)} sub="packing rounds" />
        <KPICard
          label="Avg Speed"
          value={avgOPH !== null ? `${avgOPH.toFixed(1)}/hr` : '—'}
          sub="goal: 30 orders/hr"
          color={avgOPH !== null ? (avgOPH >= 30 ? 'green' : avgOPH >= 20 ? 'yellow' : 'red') : undefined}
        />
        <KPICard
          label="Solving Orders"
          value={String(solvingTotal)}
          sub={totalOrders > 0 ? `${Math.round(solvingTotal / totalOrders * 100)}% of total` : 'pending fixes'}
        />
      </div>

      {/* KPI row 2 — team (manager/admin) */}
      {isManager && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPICard label="Active Workers" value={String(Object.keys(byPerson).length)} sub="worked in period" />
          <KPICard label="Top Performer" value={topPerson?.name ?? '—'} sub={topPerson ? `${topPerson.orders} orders` : ''} />
          <KPICard
            label="Problematic"
            value={String(problematic.length)}
            sub="cant find · label issues"
            color={problematic.length > 10 ? 'red' : problematic.length > 3 ? 'yellow' : 'green'}
          />
          <KPICard label="Team Total" value={String(totalOrders)} sub={`${totalRounds} rounds`} />
        </div>
      )}

      {!isManager && <div className="mb-6" />}

      {/* Returns throughput */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-white mb-2">Returns throughput</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <KPICard label="Returns accepted" value={retSpeed ? String(retSpeed.accepting.orders) : '—'}
            sub={retSpeed ? `${retSpeed.accepting.items} pcs · ${retSpeed.accepting.minutes} min` : ''} />
          <KPICard label="Accepting speed" value={retSpeed?.accepting.orders_per_hour != null ? `${retSpeed.accepting.orders_per_hour}/hr` : '—'} sub="orders / hour" />
          <KPICard label="Accepting items" value={retSpeed?.accepting.items_per_hour != null ? `${retSpeed.accepting.items_per_hour}/hr` : '—'} sub="items / hour" />
          <KPICard label="Polished items" value={retSpeed ? String(retSpeed.polishing.items) : '—'}
            sub={retSpeed ? `${retSpeed.polishing.sessions} sessions · ${retSpeed.polishing.minutes} min` : ''} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard label="Polishing speed" value={retSpeed?.polishing.items_per_hour != null ? `${retSpeed.polishing.items_per_hour}/hr` : '—'} sub="items / hour" />
          <KPICard label="Replenished items" value={retSpeed ? String(retSpeed.replenishment.items) : '—'}
            sub={retSpeed ? `${retSpeed.replenishment.sessions} sessions · ${retSpeed.replenishment.minutes} min` : ''} />
          <KPICard label="Replenishment speed" value={retSpeed?.replenishment.items_per_hour != null ? `${retSpeed.replenishment.items_per_hour}/hr` : '—'} sub="items / hour" />
          <div />
        </div>
      </div>
    </>
  );
}
